import { useMemo } from 'react';
import { faCalendar } from '@fortawesome/pro-solid-svg-icons/faCalendar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Anchor, Checkbox, Group, Stack, TextInput } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { DateTime } from 'luxon';
import { useIntl } from 'react-intl';

import { shipFromStepMessages } from './ship-from-step-messages';
import { formatTimeDisplay } from './ship-from-step-utils';
import styles from './ShipFromStep.module.css';
import { getTimeZoneAbbreviation } from '../../../../utils/datetime';
import { Dropdown } from '../../../common/Dropdown';
import { freightFormMessages } from '../../../freight-browser/components/freight-form/freight-form-messages';
import { ServicesGrid } from '../services-grid/ServicesGrid';

import type { ShipFromOption } from '../../../freight-browser/freight-browser-types';
import type { FreightBookingFormState, WizardPickupServices } from '../../freight-booking-wizard-types';
import type { ServicesGridField } from '../services-grid/ServicesGrid';
import type { MessageDescriptor } from 'react-intl';

const PICKUP_FIELDS: ServicesGridField<WizardPickupServices>[] = [
  { key: 'isInsidePickup', label: freightFormMessages.accessorialInsidePickup },
  { key: 'isConstructionSitePickup', label: freightFormMessages.accessorialConstructionSitePickup },
  { key: 'isLiftgatePickup', label: freightFormMessages.accessorialLiftgatePickup },
  { key: 'isNotifyBeforePickup', label: freightFormMessages.accessorialNotifyBeforePickup },
  { key: 'isResidentialPickup', label: freightFormMessages.accessorialResidentialPickup },
  { key: 'isDropAtTerminal', label: freightFormMessages.accessorialDropShipmentAtTerminal },
  { key: 'isGroceryConsolidationPickup', label: freightFormMessages.accessorialGroceryConsolidationPickup },
  { key: 'isLimitedAccess', label: freightFormMessages.accessorialLimitedAccess },
  { key: 'isTradeshowPickup', label: freightFormMessages.accessorialTradeshowPickup },
];

export type ShipFromStepProps = {
  formState: FreightBookingFormState;
  onFormChange: (next: FreightBookingFormState) => void;
  shipFromOptions: ShipFromOption[];
  onAddShipFrom?: () => void;
};

export const ShipFromStep = ({ formState, onFormChange, shipFromOptions, onAddShipFrom }: ShipFromStepProps) => {
  const intl = useIntl();

  const selectData = shipFromOptions.map((option) => ({ value: option.id, label: option.name }));

  const selectedOption = shipFromOptions.find((o) => o.id === formState.selectedShipFromId);
  const tzAbbreviation = getTimeZoneAbbreviation(selectedOption?.address?.ianaTimeZone, formState.shipmentDate);

  const handleShipFromChange = (value: string | null) => onFormChange({ ...formState, selectedShipFromId: value ?? '' });

  const handleShipDateChange = (value: string | null) => onFormChange({ ...formState, shipmentDate: value ?? '' });

  const today = useMemo(() => DateTime.now().startOf('day').toJSDate(), []);

  const handleTradeshowPickupField = (field: keyof FreightBookingFormState['tradeshowPickup'], value: string) =>
    onFormChange({ ...formState, tradeshowPickup: { ...formState.tradeshowPickup, [field]: value } });

  return (
    <Stack gap='md'>
      <div className={styles.topRow}>
        <div>
          <div className={styles.fieldLabelRow}>
            <span className={styles.fieldLabel}>{intl.formatMessage(shipFromStepMessages.selectShipFromLabel)}</span>
            <Anchor className={styles.addLink} component='button' onClick={onAddShipFrom} size='sm' type='button'>
              {intl.formatMessage(shipFromStepMessages.addShipFrom)}
            </Anchor>
          </div>
          <Dropdown
            data={selectData}
            data-testid='ship-from-select'
            onChange={handleShipFromChange}
            placeholder={intl.formatMessage(shipFromStepMessages.shipFromPlaceholder)}
            value={formState.selectedShipFromId || null}
          />
        </div>
        <DatePickerInput
          data-testid='ship-date-input'
          label={intl.formatMessage(shipFromStepMessages.shipDateLabel)}
          minDate={today}
          onChange={handleShipDateChange}
          placeholder={intl.formatMessage(shipFromStepMessages.shipDatePlaceholder)}
          rightSection={<FontAwesomeIcon icon={faCalendar} size='sm' />}
          value={formState.shipmentDate ? DateTime.fromISO(formState.shipmentDate).toJSDate() : null}
          withAsterisk
        />
      </div>

      <Group grow>
        <TextInput
          data-testid='pickup-ready-time'
          description={tzAbbreviation || undefined}
          label={intl.formatMessage(shipFromStepMessages.pickupReadyTimeLabel)}
          onChange={(event) => onFormChange({ ...formState, pickupReadyTime: event.currentTarget.value })}
          type='time'
          value={formState.pickupReadyTime}
        />
        <TextInput
          data-testid='pickup-close-time'
          description={tzAbbreviation || undefined}
          label={intl.formatMessage(shipFromStepMessages.pickupCloseTimeLabel)}
          onChange={(event) => onFormChange({ ...formState, pickupCloseTime: event.currentTarget.value })}
          type='time'
          value={formState.pickupCloseTime}
        />
      </Group>

      <Checkbox
        checked={formState.willArrangePickupSeparately}
        data-testid='arrange-pickup-separately'
        label={intl.formatMessage(shipFromStepMessages.arrangePickupSeparately)}
        onChange={(event) => onFormChange({ ...formState, willArrangePickupSeparately: event.currentTarget.checked })}
      />

      <span className={styles.servicesHeader}>{intl.formatMessage(shipFromStepMessages.pickupServicesHeader)}</span>

      <ServicesGrid
        fields={PICKUP_FIELDS}
        onChange={(next) => onFormChange({ ...formState, pickupServices: next })}
        value={formState.pickupServices}
      />

      {formState.pickupServices.isTradeshowPickup && (
        <Group grow>
          <TextInput
            data-testid='tradeshow-pickup-name'
            label={intl.formatMessage(shipFromStepMessages.tradeshowNameLabel)}
            onChange={(event) => handleTradeshowPickupField('name', event.currentTarget.value)}
            value={formState.tradeshowPickup.name}
          />
          <TextInput
            data-testid='tradeshow-pickup-booth'
            label={intl.formatMessage(shipFromStepMessages.boothNumberLabel)}
            onChange={(event) => handleTradeshowPickupField('boothNumber', event.currentTarget.value)}
            value={formState.tradeshowPickup.boothNumber}
          />
        </Group>
      )}
    </Stack>
  );
};

const formatShipDate = (isoDate: string): string => {
  if (!isoDate) return '';
  const parsed = DateTime.fromISO(isoDate);
  return parsed.isValid ? parsed.toLocaleString(DateTime.DATE_FULL) : isoDate;
};

const formatShipFromAddress = (address: ShipFromOption['address']): string => {
  if (!address) return '';
  const stateZip = [address.state, address.postalCode].filter(Boolean).join(' ');
  const cityStateZip = [address.city, stateZip].filter(Boolean).join(', ');
  return [address.line1, address.line2, cityStateZip, address.countryCode].filter(Boolean).join(', ');
};

const PICKUP_FIELD_BY_KEY: Record<keyof WizardPickupServices, MessageDescriptor> = PICKUP_FIELDS.reduce(
  (accumulator, field) => ({ ...accumulator, [field.key]: field.label }),
  {} as Record<keyof WizardPickupServices, MessageDescriptor>,
);

export type ShipFromStepSummaryProps = {
  formState: FreightBookingFormState;
  shipFromOptions: ShipFromOption[];
  pickupReadyTime?: string;
  pickupCloseTime?: string;
};

export const ShipFromStepSummary = ({ formState, shipFromOptions, pickupReadyTime, pickupCloseTime }: ShipFromStepSummaryProps) => {
  const intl = useIntl();

  const selectedShipFrom = shipFromOptions.find((option) => option.id === formState.selectedShipFromId);
  const shipDate = formatShipDate(formState.shipmentDate);
  const address = formatShipFromAddress(selectedShipFrom?.address);

  const tzAbbreviation = getTimeZoneAbbreviation(selectedShipFrom?.address?.ianaTimeZone, formState.shipmentDate);

  const hasPickupWindow = !!(pickupReadyTime || pickupCloseTime);
  const pickupWindowDisplay = hasPickupWindow
    ? intl.formatMessage(shipFromStepMessages.summaryPickupWindow, {
        from: formatTimeDisplay(pickupReadyTime ?? ''),
        to: formatTimeDisplay(pickupCloseTime ?? ''),
        tz: tzAbbreviation ? ` ${tzAbbreviation}` : '',
      })
    : '';

  const activeServices = PICKUP_FIELDS.filter((field) => formState.pickupServices[field.key]).map((field) =>
    intl.formatMessage(PICKUP_FIELD_BY_KEY[field.key]),
  );

  const hasTradeshow =
    formState.pickupServices.isTradeshowPickup && (formState.tradeshowPickup.name || formState.tradeshowPickup.boothNumber);

  const tradeshowParts = [
    formState.tradeshowPickup.name
      ? intl.formatMessage(shipFromStepMessages.summaryTradeshowName, { name: formState.tradeshowPickup.name })
      : '',
    formState.tradeshowPickup.boothNumber
      ? intl.formatMessage(shipFromStepMessages.summaryBoothNumber, { booth: formState.tradeshowPickup.boothNumber })
      : '',
  ].filter(Boolean);

  return (
    <div className={styles.summary}>
      {selectedShipFrom && (
        <div className={styles.summarySection}>
          <span className={styles.summaryLabel}>{intl.formatMessage(shipFromStepMessages.summaryLocationLabel)}</span>
          <span className={styles.summaryValue}>{selectedShipFrom.name}</span>
          {address && <span className={styles.summarySecondary}>{address}</span>}
        </div>
      )}
      {shipDate && (
        <div className={styles.summarySection}>
          <span className={styles.summaryLabel}>{intl.formatMessage(shipFromStepMessages.shipDateLabel)}</span>
          <span className={styles.summaryValue}>{shipDate}</span>
        </div>
      )}
      {hasPickupWindow && (
        <div className={styles.summarySection}>
          <span className={styles.summaryLabel}>{intl.formatMessage(shipFromStepMessages.pickupWindowLabel)}</span>
          <span className={styles.summaryValue}>{pickupWindowDisplay}</span>
        </div>
      )}
      {activeServices.length > 0 && (
        <div className={styles.summarySection}>
          <span className={styles.summaryLabel}>{intl.formatMessage(shipFromStepMessages.pickupServicesHeader)}</span>
          {/* TODO: Mantine Badge? Or at the very least consolidate these */}
          <div className={styles.tags}>
            {activeServices.map((service) => (
              <span className={styles.tag} key={service}>
                {service}
              </span>
            ))}
          </div>
          {hasTradeshow && <span className={styles.summarySecondary}>{tradeshowParts.join(' • ')}</span>}
        </div>
      )}
    </div>
  );
};
